import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Badge, Alert, Row, Col } from "react-bootstrap";
import FormField from "../components/FormField";
import LoaderButton from "../components/LoaderButton";
import { useDispatch, useSelector } from "react-redux";
import {
  adminGetAllUsers,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
  adminReactivateUser,
  adminResetPassword,
  adminGetAllLabs,
} from "../actions/userActions";

const ManageUsers = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.userLogin);
  const { users, loading } = useSelector((state) => state.adminUsers);
  const { labs } = useSelector((state) => state.adminLabs);
  const { loading: mutationLoading, success, error: mutationError } = useSelector((state) => state.adminUserMutation);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showResetPassModal, setShowResetPassModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);

  // Create form
  const [createForm, setCreateForm] = useState({
    userId: "", name: "", email: "", password: "", mobile: "", company: "", role: "user"
  });

  // Edit form
  const [editForm, setEditForm] = useState({
    id: "", user_name: "", email: "", mobile: "", company: "", role: "user"
  });

  // Reset password
  const [newPassword, setNewPassword] = useState("");

  const role = userInfo?.role || 'user';

  useEffect(() => {
    dispatch(adminGetAllUsers());
    dispatch(adminGetAllLabs());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      setAlertMsg({ type: "success", text: "Operation completed successfully" });
      setShowCreateModal(false);
      setShowEditModal(false);
      setShowResetPassModal(false);
      setTimeout(() => setAlertMsg(null), 3000);
    }
    if (mutationError) {
      setAlertMsg({ type: "danger", text: mutationError });
      setTimeout(() => setAlertMsg(null), 5000);
    }
  }, [success, mutationError]);

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    dispatch(adminCreateUser(createForm));
    setCreateForm({ userId: "", name: "", email: "", password: "", mobile: "", company: "", role: "user" });
  };

  const handleEditSubmit = (e) => {
    e.preventDefault();
    dispatch(adminUpdateUser(editForm));
  };

  const handleResetPassword = (e) => {
    e.preventDefault();
    dispatch(adminResetPassword(selectedUser.id, newPassword));
    setNewPassword("");
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      id: user.id,
      user_name: user.user_name,
      email: user.email,
      mobile: user.mobile || "",
      company: user.company || "",
      role: user.role || "user",
    });
    setShowEditModal(true);
  };

  const openResetPassModal = (user) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowResetPassModal(true);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin': return <Badge variant="danger">Admin</Badge>;
      case 'lab': return <Badge variant="info">Lab</Badge>;
      default: return <Badge variant="secondary">User</Badge>;
    }
  };

  const getStatusBadge = (isActive) => {
    return isActive ? <Badge variant="success">Active</Badge> : <Badge variant="warning">Inactive</Badge>;
  };

  return (
    <div className="manage-users-screen pb-5 mt-3">
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h3 className="font-weight-bold grey-900 mb-1" style={{ letterSpacing: '-0.8px', fontSize: 'clamp(1rem, 4vw, 1.4rem)' }}>Manage Users</h3>
          <p className="text-muted mb-0">Control system access and user roles</p>
        </div>
        <Button
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px', minWidth: '180px' }}
        >
          <i className="fa fa-user-plus mr-2"></i> Create User
        </Button>
      </div>

      {alertMsg && (
        <Alert variant={alertMsg.type} className="custom-card-boxshadow mb-4" dismissible onClose={() => setAlertMsg(null)}>
          {alertMsg.text}
        </Alert>
      )}

      <div className="billing-form-card custom-card-boxshadow p-0 overflow-hidden">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status"></div>
            <p className="mt-3 text-muted">Loading user database...</p>
          </div>
        ) : (
          <div className="premium-table-container">
            <div className="premium-table-header d-flex justify-content-between align-items-center">
              <span className="small font-weight-bold text-muted text-uppercase letter-spacing-1">Current Users</span>
            </div>
            <div className="table-responsive">
              <Table hover className="mb-0 border-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th className="text-center py-3 border-0" width="60">#</th>
                    <th className="py-3 border-0">Name</th>
                    <th className="py-3 border-0">Email</th>
                    <th className="py-3 border-0">Mobile</th>
                    <th className="py-3 border-0">Lab Name</th>
                    <th className="py-3 border-0">Role</th>
                    <th className="py-3 border-0">Status</th>
                    {role === 'admin' && <th className="text-center py-3 border-0" width="220">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {users && users.filter(u => u.role !== 'admin').length > 0 ? users.filter(u => u.role !== 'admin').map((user, index) => (
                    <tr key={user.id}>
                      <td className="text-center align-middle border-bottom font-weight-bold">{index + 1}</td>
                      <td className="align-middle border-bottom font-weight-bold">{user.user_name}</td>
                      <td className="align-middle border-bottom text-primary font-weight-medium">{user.email}</td>
                      <td className="align-middle border-bottom">{user.mobile}</td>
                      <td className="align-middle border-bottom font-italic small">{user.company_name || user.company}</td>
                      <td className="align-middle border-bottom">{getRoleBadge(user.role)}</td>
                      <td className="align-middle border-bottom">{getStatusBadge(user.is_active)}</td>
                      {role === 'admin' && (
                        <td className="text-center align-middle border-bottom">
                          <div className="d-flex justify-content-center align-items-center" style={{ gap: '15px' }}>
                            {role === 'admin' && (
                              <>
                                <Button
                                  variant="link"
                                  title="Edit User"
                                  className="p-0 text-warning"
                                  onClick={() => openEditModal(user)}
                                >
                                  <i className="fa fa-pencil-square fa-lg"></i>
                                </Button>
                                <Button
                                  variant="link"
                                  title="Reset Password"
                                  className="p-0 text-info"
                                  onClick={() => openResetPassModal(user)}
                                >
                                  <i className="fa fa-key fa-lg"></i>
                                </Button>
                                {user.is_active ? (
                                  <Button
                                    variant="link"
                                    title="Deactivate"
                                    className="p-0 text-danger"
                                    onClick={() => {
                                      if (window.confirm(`Deactivate user ${user.user_name}?`)) {
                                        dispatch(adminDeleteUser(user.id));
                                      }
                                    }}
                                  >
                                    <i className="fa fa-user-times fa-lg"></i>
                                  </Button>
                                ) : (
                                  <Button
                                    variant="link"
                                    title="Reactivate"
                                    className="p-0 text-success"
                                    onClick={() => dispatch(adminReactivateUser(user.id))}
                                  >
                                    <i className="fa fa-user-plus fa-lg"></i>
                                  </Button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  )) : (
                    <tr><td colSpan="8" className="text-center p-5 text-muted">No users found</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>

      <Modal centered show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg" backdrop="static">
        <Modal.Header className="bg-light pb-3" closeButton>
          <Modal.Title className="font-weight-bold grey-900">Create New System User</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateSubmit}>
          <Modal.Body className="p-4">
            <Row>
              <Col md={6}>
                <FormField label="Full Name" mandatory>
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    required
                    placeholder="Enter name"
                    value={createForm.name}
                    onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Email Address" mandatory>
                  <Form.Control
                    type="email"
                    className="bg-light border-0"
                    required
                    placeholder="name@example.com"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Access Password" mandatory>
                  <Form.Control
                    type="password"
                    className="bg-light border-0"
                    required
                    minLength={8}
                    placeholder="Min 8 characters"
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Mobile Number">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Optional"
                    value={createForm.mobile}
                    onChange={(e) => setCreateForm({ ...createForm, mobile: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Assign Laboratory" mandatory>
                  <Form.Control
                    as="select"
                    className="bg-light border-0"
                    required
                    value={createForm.company}
                    onChange={(e) => setCreateForm({ ...createForm, company: e.target.value })}
                  >
                    <option value="">-- Choose Lab --</option>
                    {labs && labs.map(lab => (
                      <option key={lab.id} value={lab.id}>
                        {lab.com_name}
                      </option>
                    ))}
                  </Form.Control>
                </FormField>
              </Col>
              {role === 'admin' && (
                <Col md={6}>
                  <FormField label="User Role" mandatory>
                    <Form.Control
                      as="select"
                      className="bg-light border-0"
                      value={createForm.role}
                      onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                    >
                      <option value="user">User</option>
                      <option value="lab">Lab</option>
                      <option value="admin">Admin</option>
                    </Form.Control>
                  </FormField>
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-light border-0 p-3">
            <Button variant="outline-secondary" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }} onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <LoaderButton isLoading={mutationLoading} variant="primary" type="submit">
              Store User Details
            </LoaderButton>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal centered show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" backdrop="static">
        <Modal.Header className="bg-light pb-3" closeButton>
          <Modal.Title className="font-weight-bold grey-900">Update User Profile: {selectedUser?.user_name}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body className="p-4">
            <Row>
              <Col md={6}>
                <FormField label="Full Name">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Name"
                    value={editForm.user_name}
                    onChange={(e) => setEditForm({ ...editForm, user_name: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Email Address">
                  <Form.Control
                    type="email"
                    className="bg-light border-0"
                    placeholder="Email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Mobile Number">
                  <Form.Control
                    type="text"
                    className="bg-light border-0"
                    placeholder="Mobile"
                    value={editForm.mobile}
                    onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })}
                  />
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="Assigned Laboratory">
                  <Form.Control
                    as="select"
                    className="bg-light border-0"
                    value={editForm.company}
                    onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
                  >
                    <option value="">-- Choose Lab --</option>
                    {labs && labs.map(lab => (
                      <option key={lab.id} value={lab.id}>
                        {lab.com_name}
                      </option>
                    ))}
                  </Form.Control>
                </FormField>
              </Col>
              <Col md={6}>
                <FormField label="System Role">
                  <Form.Control
                    as="select"
                    className="bg-light border-0"
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                  >
                    <option value="user">User</option>
                    <option value="lab">Lab</option>
                    <option value="admin">Admin</option>
                  </Form.Control>
                </FormField>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className="bg-light border-0 p-3">
            <Button variant="outline-secondary" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }} onClick={() => setShowEditModal(false)}>Cancel</Button>
            <LoaderButton isLoading={mutationLoading} variant="primary" type="submit">
              Update User
            </LoaderButton>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal centered show={showResetPassModal} onHide={() => setShowResetPassModal(false)} backdrop="static">
        <Modal.Header className="bg-light pb-3" closeButton>
          <Modal.Title className="font-weight-bold grey-900 text-warning">
            <i className="fa fa-key mr-2"></i> Reset Password
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleResetPassword}>
          <Modal.Body className="p-4">
            <p className="text-muted small mb-4">Resetting password for <strong>{selectedUser?.user_name}</strong>. Please ensure the new password is secure.</p>
            <FormField label="Set New Secure Password" mandatory>
              <Form.Control
                type="password"
                className="bg-light border-0"
                required
                minLength={8}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Minimum 8 characters..."
              />
            </FormField>
          </Modal.Body>
          <Modal.Footer className="bg-light border-0 p-3">
            <Button variant="outline-secondary" style={{ fontWeight: 600, height: '40px', padding: '0 24px', borderRadius: '8px' }} onClick={() => setShowResetPassModal(false)}>Cancel</Button>
            <LoaderButton isLoading={mutationLoading} variant="warning" type="submit" style={{ color: '#fff' }}>
              Update Credentials
            </LoaderButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default ManageUsers;
